import React from "react";
import PropTypes from "prop-types";
import Yup from "yup";
import { Form, Formik } from "formik";
import { get, has } from "lodash";
import { withStyles } from "@mui/styles";
import { FormControl, Button, TextField, FormHelperText } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SimpleSnackbar from "common/components/SimpleSnackbar";
import { hasError, getError } from "common/helpers/formik";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const validationSchema = (t) =>
  Yup.object().shape({
    name: Yup.string().required(t("contact_topics_create.name_error")),
    contact_email: Yup.string()
      .transform((value) => Array.from(new Set(value.split(","))).join(","))
      .required(t("contact_topics_create.email_error"))
      .test(
        "emails",
        t("contact_topics_create.email_invalid"),
        (value) => value && value.split(",").every(isEmail),
      ),
  });

const ContactTopicForm = ({ classes, handleSubmit, initialValues, isEdit }) => {
  const { t } = useTranslation();
  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema(t)}
      onSubmit={handleSubmit}
      initialValues={initialValues}
    >
      {({ values, errors, touched, isSubmitting, handleChange, status }) => {
        const checkError = hasError(errors, touched);
        const errorMsg = getError(errors, touched);
        // const contactEmailValue = compact(
        //   (initialValues.contact_email || "").split(","),
        // );
        return (
          <Form>
            <Grid container direction="column" spacing={2}>
              <Grid size={{ xs: 8 }}>
                <FormControl className={classes.formControl}>
                  <TextField
                    error={checkError("name")}
                    id="name"
                    type="text"
                    label={t("contact_topics_create.name_label")}
                    margin="normal"
                    helperText={errorMsg("name")}
                    onChange={handleChange}
                    value={values.name}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ xs: 8 }}>
                <FormControl className={classes.formControl}>
                  <Autocomplete
                    multiple
                    freeSolo
                    id="contact_email"
                    name="contact_email"
                    options={[]} // No predefined options
                    value={
                      values.contact_email
                        ? values.contact_email.split(",")
                        : []
                    }
                    onChange={(event, newValue) => {
                      handleChange({
                        target: {
                          id: "contact_email",
                          name: "contact_email",
                          value: newValue.join(","),
                        },
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t("contact_topics_create.email_label")}
                        id="contact_email"
                        name="contact_email"
                        error={checkError("contact_email")}
                        helperText={errorMsg("contact_email")}
                      />
                    )}
                  />

                  {touched.contact_email && has(errors, "contact_email") ? (
                    <FormHelperText error>
                      {errors.contact_email}
                    </FormHelperText>
                  ) : (
                    <FormHelperText>
                      {t("contact_topics_create.email_hint")}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.button}
                disabled={isSubmitting}
              >
                {t(
                  isEdit
                    ? "contact_topics_create.update_btn"
                    : "contact_topics_create.submit_btn",
                )}
              </Button>
              {has(errors, "http") && (
                <SimpleSnackbar
                  message={t("general_form_errors.error_response")}
                />
              )}
            </Grid>
            {get(status, "success") && (
              <SimpleSnackbar
                message={t(
                  isEdit
                    ? "contact_topics_create.update_success"
                    : "contact_topics_create.create_success",
                )}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

const styles = (theme) => ({
  button: {
    marginTop: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    width: "100%",
  },
});

ContactTopicForm.propTypes = {
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  isEdit: PropTypes.bool,
};

export default withStyles(styles)(ContactTopicForm);
